(() => {
  'use strict';

  /*
   * INSTELLINGEN
   */
  const SETTINGS = {
    giftVariantId: 62638533706058,

    // Bedragen zijn in centen
    giftMinimum: 5000,          // €50
    reducedShippingMinimum: 4500, // €45
    freeShippingMinimum: 7500,    // €75

    standardShippingPrice: 1000, // €10
    reducedShippingPrice: 495,   // €4,95

    // Na toevoegen/verwijderen van het cadeau wordt de pagina
    // vernieuwd zodat Horizon de cart drawer correct bijwerkt.
    reloadAfterGiftChange: true
  };

  let giftUpdateInProgress = false;
  let updateTimer = null;
  let lastCartSignature = '';

  /*
   * SHOPIFY-ROUTE
   * Hierdoor blijft de code ook werken wanneer de webshop
   * een taalpad gebruikt, zoals /nl/cart.js.
   */
  function getRootRoute() {
    return window.Shopify?.routes?.root || '/';
  }

  function cartUrl(endpoint) {
    const root = getRootRoute();
    return `${root}cart/${endpoint}`;
  }

  /*
   * GELD OPMAKEN
   */
  function formatMoney(cents) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  /*
   * WINKELWAGEN OPHALEN
   */
  async function getCart() {
    const response = await fetch(cartUrl('js'), {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`Winkelwagen kon niet worden opgehaald: ${response.status}`);
    }

    return response.json();
  }

  /*
   * SUBTOTAAL ZONDER GRATIS CADEAU
   */
  function getSubtotalWithoutGift(cart) {
    return cart.items
      .filter(
        item => Number(item.variant_id) !== Number(SETTINGS.giftVariantId)
      )
      .reduce((total, item) => {
        /*
         * final_line_price houdt rekening met eventuele
         * productkortingen op de winkelwagenregels.
         */
        return total + Number(item.final_line_price || 0);
      }, 0);
  }

  function getGiftItems(cart) {
    return cart.items.filter(
      item => Number(item.variant_id) === Number(SETTINGS.giftVariantId)
    );
  }

  /*
   * CADEAU TOEVOEGEN
   */
  async function addGift() {
    const response = await fetch(cartUrl('add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: SETTINGS.giftVariantId,
            quantity: 1,
            properties: {
              _automatic_free_gift: 'true'
            }
          }
        ]
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Cadeau kon niet worden toegevoegd: ${errorText}`);
    }

    return response.json();
  }

  /*
   * CADEAU VERWIJDEREN OF AANTAL OP 1 ZETTEN
   */
  async function changeCartLine(lineKey, quantity) {
    const response = await fetch(cartUrl('change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Cadeau kon niet worden aangepast: ${errorText}`);
    }

    return response.json();
  }

  /*
   * BANNERELEMENT MAKEN
   */
  function createShippingBanner() {
    const banner = document.createElement('div');

    banner.id = 'automatic-shipping-banner';
    banner.className = 'automatic-shipping-banner';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');

    banner.innerHTML = `
      <div class="automatic-shipping-banner__message">
        Verzendinformatie wordt berekend…
      </div>

      <div
        class="automatic-shipping-banner__progress"
        aria-hidden="true"
      >
        <div class="automatic-shipping-banner__progress-fill"></div>

        <span
          class="automatic-shipping-banner__marker automatic-shipping-banner__marker--reduced"
        ></span>

        <span
          class="automatic-shipping-banner__marker automatic-shipping-banner__marker--free"
        ></span>
      </div>

      <div class="automatic-shipping-banner__labels">
        <span>€0</span>
        <span>€45</span>
        <span>€75</span>
      </div>
    `;

    return banner;
  }

  /*
   * PROBEREN DE CHECKOUTKNOP OF CART FOOTER TE VINDEN
   *
   * De selectors zijn bewust uitgebreid, zodat dit in verschillende
   * versies van Horizon en verschillende cart drawers kan werken.
   */
  function findBannerLocation() {
    const checkoutSelectors = [
      'cart-drawer button[name="checkout"]',
      'cart-drawer a[href*="/checkout"]',
      'cart-drawer-component button[name="checkout"]',
      'cart-drawer-component a[href*="/checkout"]',
      '[data-cart-drawer] button[name="checkout"]',
      '[data-cart-drawer] a[href*="/checkout"]',
      '.cart-drawer button[name="checkout"]',
      '.cart-drawer a[href*="/checkout"]',
      '#CartDrawer button[name="checkout"]',
      '#CartDrawer a[href*="/checkout"]',
      '[role="dialog"] button[name="checkout"]',
      '[role="dialog"] a[href*="/checkout"]',
      'form[action*="/cart"] button[name="checkout"]',
      'form[action*="/cart"] a[href*="/checkout"]'
    ];

    for (const selector of checkoutSelectors) {
      const element = document.querySelector(selector);

      if (element && isVisibleOrInDrawer(element)) {
        return {
          type: 'before',
          element
        };
      }
    }

    const footerSelectors = [
      'cart-drawer .cart-drawer__footer',
      'cart-drawer-component .cart-drawer__footer',
      '[data-cart-drawer] .cart-drawer__footer',
      '.cart-drawer__footer',
      '#CartDrawer .drawer__footer',
      '[role="dialog"] .drawer__footer',
      '.cart__footer',
      '[data-cart-footer]'
    ];

    for (const selector of footerSelectors) {
      const element = document.querySelector(selector);

      if (element) {
        return {
          type: 'prepend',
          element
        };
      }
    }

    return null;
  }

  function isVisibleOrInDrawer(element) {
    if (!element) return false;

    return Boolean(
      element.offsetParent ||
      element.closest(
        'cart-drawer, cart-drawer-component, [data-cart-drawer], .cart-drawer, #CartDrawer, [role="dialog"]'
      )
    );
  }

  /*
   * BANNER IN DE CART DRAWER PLAATSEN
   */
  function ensureShippingBanner() {
    let banner = document.querySelector('#automatic-shipping-banner');

    /*
     * Wanneer Horizon de cart drawer opnieuw rendert,
     * kan de oude banner uit de DOM verdwijnen.
     */
    if (banner && document.body.contains(banner)) {
      return banner;
    }

    const location = findBannerLocation();

    if (!location) {
      return null;
    }

    banner = createShippingBanner();

    if (location.type === 'before') {
      location.element.parentNode.insertBefore(banner, location.element);
    } else {
      location.element.prepend(banner);
    }

    return banner;
  }

  /*
   * TEKST EN VOORTGANG BIJWERKEN
   */
  function updateShippingBanner(cart) {
    const banner = ensureShippingBanner();

    if (!banner) return;

    const subtotal = getSubtotalWithoutGift(cart);
    const messageElement = banner.querySelector(
      '.automatic-shipping-banner__message'
    );
    const progressFill = banner.querySelector(
      '.automatic-shipping-banner__progress-fill'
    );

    let message = '';

    if (subtotal < SETTINGS.reducedShippingMinimum) {
      const remaining =
        SETTINGS.reducedShippingMinimum - subtotal;

      message = `
        Nog <strong>${formatMoney(remaining)}</strong>
        voor lagere verzendkosten.
        Daarna betaal je
        <strong>${formatMoney(SETTINGS.reducedShippingPrice)}</strong>
        verzending in plaats van
        ${formatMoney(SETTINGS.standardShippingPrice)}.
      `;
    } else if (subtotal < SETTINGS.freeShippingMinimum) {
      const remaining =
        SETTINGS.freeShippingMinimum - subtotal;

      message = `
        Je verzendkosten zijn nu
        <strong>${formatMoney(SETTINGS.reducedShippingPrice)}</strong>.
        Nog <strong>${formatMoney(remaining)}</strong>
        voor gratis verzending.
      `;
    } else {
      message = `
        <strong>🎉 Je bestelling wordt gratis verzonden!</strong>
      `;
    }

    const progress = Math.min(
      (subtotal / SETTINGS.freeShippingMinimum) * 100,
      100
    );

    messageElement.innerHTML = message;
    progressFill.style.width = `${progress}%`;

    banner.classList.toggle(
      'automatic-shipping-banner--free',
      subtotal >= SETTINGS.freeShippingMinimum
    );
  }

  /*
   * CONTROLEREN OF HET CADEAU MOET WORDEN
   * TOEGEVOEGD, VERWIJDERD OF GECORRIGEERD
   */
  async function synchronizeGift(cart) {
    if (giftUpdateInProgress) {
      return false;
    }

    const subtotalWithoutGift = getSubtotalWithoutGift(cart);
    const giftItems = getGiftItems(cart);
    const qualifiesForGift =
      subtotalWithoutGift >= SETTINGS.giftMinimum;

    giftUpdateInProgress = true;

    try {
      /*
       * Klant heeft recht op cadeau en cadeau ontbreekt.
       */
      if (qualifiesForGift && giftItems.length === 0) {
        await addGift();
        return true;
      }

      /*
       * Klant heeft geen recht meer op het cadeau.
       */
      if (!qualifiesForGift && giftItems.length > 0) {
        for (const giftItem of giftItems) {
          await changeCartLine(giftItem.key, 0);
        }

        return true;
      }

      /*
       * Voorkom dat het cadeau meerdere keren in de
       * winkelwagen staat of een hoeveelheid hoger dan 1 heeft.
       */
      if (qualifiesForGift && giftItems.length > 0) {
        const firstGift = giftItems[0];

        if (Number(firstGift.quantity) !== 1) {
          await changeCartLine(firstGift.key, 1);
          return true;
        }

        if (giftItems.length > 1) {
          for (let index = 1; index < giftItems.length; index += 1) {
            await changeCartLine(giftItems[index].key, 0);
          }

          return true;
        }
      }

      return false;
    } finally {
      giftUpdateInProgress = false;
    }
  }

  /*
   * HOOFDUPDATE
   */
  async function updateCartFeatures() {
    if (giftUpdateInProgress) return;

    try {
      const cart = await getCart();

      updateShippingBanner(cart);

      const cartSignature = JSON.stringify(
        cart.items.map(item => [
          item.key,
          item.quantity,
          item.final_line_price
        ])
      );

      lastCartSignature = cartSignature;

      const giftWasChanged = await synchronizeGift(cart);

      if (giftWasChanged) {
        if (SETTINGS.reloadAfterGiftChange) {
          window.location.reload();
          return;
        }

        const updatedCart = await getCart();
        updateShippingBanner(updatedCart);
      }
    } catch (error) {
      console.error('[Gratis cadeau/verzendbanner]', error);
    }
  }

  function scheduleUpdate(delay = 300) {
    window.clearTimeout(updateTimer);

    updateTimer = window.setTimeout(() => {
      updateCartFeatures();
    }, delay);
  }

  /*
   * CART-VERZOEKEN HERKENNEN
   *
   * Wanneer Horizon een product toevoegt, verwijdert of wijzigt,
   * wordt de verzendbanner daarna opnieuw berekend.
   */
  function isCartMutationRequest(input, options = {}) {
    let url = '';
    let method = String(options.method || 'GET').toUpperCase();

    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof Request) {
      url = input.url;
      method = String(options.method || input.method || 'GET').toUpperCase();
    }

    if (method === 'GET') return false;

    return /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(url);
  }

  /*
   * FETCH AFVANGEN
   */
  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input, options = {}) {
    const isCartMutation = isCartMutationRequest(input, options);
    const response = await originalFetch(input, options);

    if (isCartMutation && !giftUpdateInProgress) {
      scheduleUpdate(450);
    }

    return response;
  };

  /*
   * OOK OUDERE XMLHttpRequest-CARTS ONDERSTEUNEN
   */
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function patchedOpen(
    method,
    url,
    ...rest
  ) {
    this.__automaticCartRequest =
      String(method).toUpperCase() !== 'GET' &&
      /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(
        String(url)
      );

    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function patchedSend(...args) {
    if (this.__automaticCartRequest) {
      this.addEventListener(
        'loadend',
        () => {
          if (!giftUpdateInProgress) {
            scheduleUpdate(450);
          }
        },
        { once: true }
      );
    }

    return originalXhrSend.apply(this, args);
  };

  /*
   * HORIZON KAN DE CART DRAWER OPNIEUW OPBOUWEN.
   * De MutationObserver plaatst de banner daarna terug.
   */
  const observer = new MutationObserver(() => {
    const banner = ensureShippingBanner();

    if (banner) {
      scheduleUpdate(150);
    }
  });

  function initialize() {
    updateCartFeatures();

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    /*
     * Extra ondersteuning voor veelgebruikte cart-events.
     */
    [
      'cart:update',
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'cart:add',
      'cart-drawer:open',
      'cart-drawer:opened'
    ].forEach(eventName => {
      document.addEventListener(eventName, () => {
        scheduleUpdate(150);
      });
    });

    /*
     * Bij een klik op cart-/aantalbediening nogmaals controleren.
     */
    document.addEventListener('click', event => {
      const cartControl = event.target.closest(
        [
          '[name="plus"]',
          '[name="minus"]',
          '[data-quantity-plus]',
          '[data-quantity-minus]',
          '[data-cart-remove]',
          'cart-remove-button',
          'button[href*="/cart/change"]',
          'a[href*="/cart/change"]',
          'a[href="/cart"]',
          '[data-cart-icon-bubble]',
          '[data-cart-toggle]'
        ].join(',')
      );

      if (cartControl) {
        scheduleUpdate(600);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();